<template>
	<Dialog :open="open" @update:open="$emit('close')">
		<DialogContent :disable-overlay-close="disableOverlayClose" class="gap-1" @close="$emit('close')">
			<DialogTitle>Upload File</DialogTitle>
			<DialogDescription>
				Upload an audio file to use in your flows. Supported file types are .mp3, .wav, .mp4, .m4a, .mp4a and
				.opus. We recommend 8khz, 16-bit mono PCM WAV files.
			</DialogDescription>
			<div
				v-if="(tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller')) && !hideTenantSelector"
				class="mx-auto w-full"
			>
				<div class="formGroup withIcon">
					<label class="textGrey">Tenant</label>
					<select v-model="selectedTenant" @change="selectedAccount = null">
						<option disabled :value="null">-- Select Tenant --</option>
						<option v-for="t in tenants" :key="t.id" :value="t.id">
							{{ t.companyName }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div v-if="selectedTenant && !hideAccountSelector" class="mx-auto w-full">
				<div class="formGroup withIcon">
					<label class="textGrey">Account</label>
					<select v-model="selectedAccount">
						<option :value="null">-- Unassigned - Tenant File --</option>
						<option v-for="a in tenantAccounts" :key="a.id" :value="a.id">
							{{ a.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div class="formGroup py-2">
				<label class="textGrey">Select File</label>
				<form enctype="multipart/form-data">
					<input
						id="file"
						type="file"
						accept=".mp3,.wav,.m4a,.mp4,.mp4a,.opus"
						name="file"
						@change="handleFileChange"
					/>
				</form>
			</div>

			<div v-if="isFileSelected" class="w-full">
				<div class="overflow-hidden rounded-md p-4 pb-0">
					<media-player :file="file" />
				</div>
				<div class="formGroup">
					<button :disabled="!selectedTenant" class="jmiBtn green" @click="uploadFile">Upload</button>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import accountsApi from '@/scripts/accounts/accounts.api'
import filesApi from '@/scripts/files/files.api'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogTitle,
		DialogDescription
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: ['disableOverlayClose', 'selectedTenantId', 'selectedAccountId', 'open', 'loadedAccounts'],
	emits: ['close'],
	data() {
		return {
			accounts: [],
			selectedTenant: this.selectedTenantId || null,
			selectedAccount: this.selectedAccountId || null,
			isFileSelected: false,
			previewedFile: null,
			hideTenantSelector: Boolean(this.selectedTenantId),
			hideAccountSelector: Boolean(this.selectedAccountId),
			isPlaying: false,
			currentTime: '00:00',
			file: null,
			totalDuration: '00:00',
			options: {
				height: 46,
				waveColor: 'gray',
				progressColor: 'green',
				barGap: 0,
				barWidth: 2,
				barRadius: 0,
				duration: 10
			}
		}
	},
	computed: {
		tenantAccounts() {
			return this.accounts
				.filter((a) => a.tenant.id === this.selectedTenant)
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	},
	watch: {
		open(newVal) {
			if (newVal) {
				if (this.selectedTenantId) {
					this.selectedTenant = this.selectedTenantId
				} else if (this.tenants?.length === 1) {
					this.selectedTenant = this.tenants[0].id
				}

				if (this.selectedAccountId) {
					this.selectedAccount = this.selectedAccountId
					this.hideAccountSelector = true
				} else {
					this.hideAccountSelector = false
				}

				this.loadAccounts()
			}
		}
	},
	async mounted() {
		await this.loadAccounts()
	},
	methods: {
		async loadAccounts() {
			try {
				if (!this.loadedAccounts) {
					const result = await accountsApi.getAll({ limit: 2500 })
					this.accounts = result.data.results
				} else {
					this.accounts = this.loadedAccounts
				}

				if (this.selectedAccountId && this.selectedTenantId) {
					this.selectedAccount = this.selectedAccountId
					this.hideAccountSelector = true
				}
			} catch (error) {
				console.error(error)
			}
		},
		handleFileChange(event) {
			this.isFileSelected = true
			this.previewedFile = event.target.files[0]
			this.file = new Blob([this.previewedFile], { type: this.previewedFile.type })
		},
		uploadFile() {
			let formData = new FormData(document.forms[0])

			filesApi[this.selectedAccount ? 'createAccountFile' : 'createTenantFile'](
				this.selectedAccount || this.selectedTenant,
				formData
			)
				.then((result) => {
					this.isFileSelected = false
					this.previewedFile = null
					this.$emit('close', result)
				})
				.catch(console.error)
		}
	}
}
</script>
